import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Collapse } from 'antd';
import GenericTable from '../tables/GenericTable';
import SurveyFieldInput from './SurveyFieldInput';
import RepeatableConditionsField from './RepeatableConditionsField';
import { buildReadRows } from './FieldValue';
import { formatDate } from '../../utils/formatDate';
import { validateSection } from '../../utils/personal-survey-validation';
import styles from '../../styles/personal-survey.module.scss';

const READ_COLUMNS = [
  { key: 'info', label: '' },
  { key: 'value', label: '' },
];

const clearHiddenFields = (fieldConfig, draft) => {
  const next = { ...draft };
  fieldConfig.forEach((field) => {
    if (field.visibleIf && !field.visibleIf(next)) {
      delete next[field.key];
    }
  });
  return next;
};

const SurveySection = ({
  title,
  fieldConfig,
  sectionData,
  completedAt,
  onSave,
  disabled,
  disabledReason,
  extraAction,
  isExpanded,
  onToggleExpand = () => {},
  canEdit = true,
  readOnly = false,
  onEditStart = () => {},
  onEditEnd = () => {},
}) => {
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleEdit = () => {
    setDraft(sectionData || {});
    setErrors({});
    setEditMode(true);
    onEditStart();
  };

  const handleCancel = () => {
    setEditMode(false);
    setErrors({});
    onEditEnd();
  };

  const handleFieldChange = (key, value) => {
    const next = clearHiddenFields(fieldConfig, { ...draft, [key]: value });
    setDraft(next);
    setErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[key];
      fieldConfig.forEach((field) => {
        if (!(field.key in next)) delete nextErrors[field.key];
      });
      return nextErrors;
    });
  };

  const handleSave = async () => {
    const validationErrors = validateSection(fieldConfig, draft);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    const success = await onSave(draft);
    setSaving(false);
    if (success) {
      setErrors({});
      setEditMode(false);
      onEditEnd();
    }
  };

  const isDone = !!sectionData;
  const visibleFields = editMode
    ? fieldConfig.filter((field) => (field.visibleIf ? field.visibleIf(draft) : true))
    : [];
  const topLevelFields = visibleFields.filter(
    (field) => !field.groupWith || !visibleFields.some((f) => f.key === field.groupWith)
  );

  const renderField = (field) => {
    const children = visibleFields.filter((f) => f.groupWith === field.key);

    return (
      <div
        className={`${styles.section__field} ${field.type === 'repeatable' ? styles['section__field--full'] : ''}`}
        key={field.key}
      >
        <label>
          {field.label}
          {field.required && <span className={styles.section__required}>*</span>}
        </label>
        {field.type === 'repeatable' ? (
          <RepeatableConditionsField
            itemFields={field.itemFields}
            value={draft[field.key]}
            onChange={(v) => handleFieldChange(field.key, v)}
          />
        ) : (
          <SurveyFieldInput
            field={field}
            value={draft[field.key]}
            onChange={(v) => handleFieldChange(field.key, v)}
            status={errors[field.key] ? 'error' : undefined}
          />
        )}
        {errors[field.key] && (
          <span className={styles.section__fieldError}>{errors[field.key]}</span>
        )}
        {children.length > 0 && (
          <div className={styles.section__nested}>{children.map((child) => renderField(child))}</div>
        )}
      </div>
    );
  };

  const headerContent = (
    <div className={styles.section__header}>
      <div className={styles.section__title}>
        <span>{title}</span>
        <span className={`${styles.badge} ${isDone ? styles['badge--done'] : styles['badge--pending']}`}>
          {isDone ? `Popunjeno${completedAt ? ' - ' + formatDate(completedAt) : ''}` : 'Nije popunjeno'}
        </span>
      </div>
      {!disabled && !editMode && !readOnly && (
        <Button
          className="btn-tertiary btn-small"
          disabled={!canEdit}
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          {isDone ? 'Uredi' : 'Popuni'}
        </Button>
      )}
    </div>
  );

  const bodyContent = (
    <>
      {!editMode && (
        isDone ? (
          <GenericTable
            data={buildReadRows(fieldConfig, sectionData)}
            columns={READ_COLUMNS}
            grayTheme
            mobNoScroll
          />
        ) : (
          <div className={styles.section__empty}>Ova sekcija još nije popunjena.</div>
        )
      )}

      {editMode && (
        <>
          <div className={styles.section__editGrid}>
            {topLevelFields.map((field) => renderField(field))}
          </div>
          <div className={styles.section__actions}>
            <Button className="btn-tertiary btn-small" onClick={handleCancel}>
              Otkaži
            </Button>
            <Button
              type="primary"
              className="btn-primary btn-small"
              onClick={handleSave}
              loading={saving}
            >
              Sačuvaj
            </Button>
          </div>
        </>
      )}
    </>
  );

  if (disabled) {
    return (
      <div className={styles.section}>
        {headerContent}
        <div className={styles.section__disabledReason}>{disabledReason}</div>
        {extraAction}
      </div>
    );
  }

  const open = isExpanded || editMode;

  return (
    <div className={styles.section}>
      <Collapse
        ghost
        collapsible="icon"
        className={styles.section__collapse}
        activeKey={open ? ['panel'] : []}
        onChange={(keys) => {
          // The only way out of an in-progress edit is Cancel/Save - never an
          // accidental (or coordinated-away) collapse-click.
          if (editMode) return;
          onToggleExpand(keys.length > 0);
        }}
        items={[{ key: 'panel', label: headerContent, children: bodyContent }]}
      />
    </div>
  );
};

SurveySection.propTypes = {
  title: PropTypes.string.isRequired,
  fieldConfig: PropTypes.array.isRequired,
  sectionData: PropTypes.object,
  completedAt: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.node,
  extraAction: PropTypes.node,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  canEdit: PropTypes.bool,
  // Staff who may read the survey but not change it (writes stay admin-only in the API).
  readOnly: PropTypes.bool,
  onEditStart: PropTypes.func,
  onEditEnd: PropTypes.func,
};

export default SurveySection;
